import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SETUP } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

/**
 * Setup / one-time employee import — presents the client-workbook-derived
 * proposals as editable, per-row confirmation UI. Nothing writes to the
 * database until the admin edits + clicks the row's "Confirm & apply" button.
 * If an employee with the same name already exists, the button becomes
 * "Update instead" and PATCHes rather than duplicating.
 *
 * This screen exists so Michael can verify each rate/dep/allowance value
 * against the client's own workbook BEFORE it lands, per the "no auto-seed"
 * rule agreed 2026-02.
 */
export default function SetupEmployees() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [rows, setRows] = useState([]); // [{proposal, existing_id, existing_emp_code, edited, saving, done}]
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/setup/proposed-employees");
            setRows(
                data.rows.map((r) => ({
                    ...r,
                    edited: {
                        ...r.proposal,
                        // Copy allowance out into a flat other_kina for the inline editor
                        allowance_kina: r.proposal.recurring_allowances?.other_kina ?? 0,
                    },
                    saving: false,
                    done: false,
                })),
            );
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Failed to load proposals");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const setEdit = (i, patch) => {
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, edited: { ...r.edited, ...patch } } : r)));
    };

    const applyRow = async (i) => {
        const row = rows[i];
        const e = row.edited;
        const payload = {
            name: e.name,
            position: e.position || "",
            emp_code: (e.emp_code || "").trim(),
            labour_rate_kina: Number(e.labour_rate_kina) || 0,
            dependants_declared: Number(e.dependants_declared) || 0,
            tax_status: e.tax_status || "resident_decl",
            recurring_allowances: {
                travel_kina: 0,
                housing_kina: 0,
                other_kina: Number(e.allowance_kina) || 0,
            },
        };
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, saving: true } : r)));
        try {
            if (row.existing_id) {
                await api.patch(`/employees/${row.existing_id}`, payload);
                toast.success(`${e.name} updated`);
            } else {
                await api.post("/employees", payload);
                toast.success(`${e.name} added`);
            }
            setRows((prev) =>
                prev.map((r, idx) => (idx === i ? { ...r, saving: false, done: true } : r)),
            );
            // Reload so the "existing_id" flips for the just-created row (idempotent re-apply)
            setTimeout(() => load(), 400);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Save failed");
            setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, saving: false } : r)));
        }
    };

    if (!isAdmin) {
        return (
            <div className="p-10 text-sm text-neutral-500" data-testid={SETUP.page}>
                Admin only.
            </div>
        );
    }

    return (
        <div className="p-10 max-w-[1400px]" data-testid={SETUP.page}>
            <div className="mb-8">
                <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                    Setup · one-time import
                </div>
                <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                    Load real employees from KP's workbook
                </h1>
                <p className="text-sm text-neutral-500 mt-2 max-w-[720px]">
                    The rows below are proposed values pulled from the client's payroll workbook
                    ("Tax Calc 2024" sheet, wages tab, dependants column). <strong>Nothing writes
                    until you click Confirm</strong>. Edit any value before applying — rate, deps,
                    allowance, tax status, and emp code are all editable inline. Rows that already
                    exist (matched by name) show <em>update instead</em>.
                </p>
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 text-[11px] rounded-[3px]"
                     style={{ background: "#FFF7E6", color: "#9A6C00", border: "1px solid #F5D583" }}>
                    <AlertTriangle size={13} /> Leave &amp; loan opening balances are NOT proposed here —
                    the client's workbook has figures for only 4 of 9 staff. Set those individually on the
                    employee record so "not supplied" stays visible for the rest.
                </div>
            </div>

            {loading && <div className="text-sm text-neutral-400">Loading proposals…</div>}

            <div className="bg-white border border-neutral-200 rounded-[3px] overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-display"
                            style={{ background: "#FAFAFA" }}>
                            <th className="px-3 py-3">Name</th>
                            <th className="px-3 py-3">Emp code</th>
                            <th className="px-3 py-3 text-right">Rate K/hr</th>
                            <th className="px-3 py-3 text-center">Deps decl.</th>
                            <th className="px-3 py-3 text-right">Allowance K/fn</th>
                            <th className="px-3 py-3">Status</th>
                            <th className="px-3 py-3 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => {
                            const e = r.edited;
                            return (
                                <tr key={i} data-testid={SETUP.row(i)} className="border-t border-neutral-100">
                                    <td className="px-3 py-2">
                                        <div className="text-[color:var(--kp-charcoal)] font-medium">{e.name}</div>
                                        {e.position && (
                                            <div className="text-[10px] text-neutral-500 mt-0.5 leading-tight max-w-[220px]">
                                                {e.position}
                                            </div>
                                        )}
                                        <div className="text-[10px] text-neutral-400 mt-0.5">source · {r.proposal.source || "workbook"}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <input
                                            data-testid={SETUP.fieldEmpCode(i)}
                                            value={e.emp_code ?? ""}
                                            onChange={(ev) => setEdit(i, { emp_code: ev.target.value })}
                                            placeholder="—"
                                            className="w-[110px] font-mono-money border border-neutral-300 rounded-[2px] px-2 py-1 text-[12px]"
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <input
                                            data-testid={SETUP.fieldRate(i)}
                                            type="number" step="0.01" min="0"
                                            value={e.labour_rate_kina ?? 0}
                                            onChange={(ev) => setEdit(i, { labour_rate_kina: ev.target.valueAsNumber })}
                                            className="w-[80px] font-mono-money border border-neutral-300 rounded-[2px] px-2 py-1 text-right text-[12px]"
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <input
                                            data-testid={SETUP.fieldDeps(i)}
                                            type="number" min="0" max="20"
                                            value={e.dependants_declared ?? 0}
                                            onChange={(ev) => setEdit(i, { dependants_declared: ev.target.valueAsNumber })}
                                            className="w-[60px] font-mono-money border border-neutral-300 rounded-[2px] px-2 py-1 text-center text-[12px]"
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <input
                                            data-testid={SETUP.fieldAllowance(i)}
                                            type="number" step="0.01" min="0"
                                            value={e.allowance_kina ?? 0}
                                            onChange={(ev) => setEdit(i, { allowance_kina: ev.target.valueAsNumber })}
                                            className="w-[80px] font-mono-money border border-neutral-300 rounded-[2px] px-2 py-1 text-right text-[12px]"
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-neutral-600 text-[12px]">
                                        {r.existing_id ? (
                                            <span data-testid={SETUP.alreadyExists(i)}
                                                  className="inline-flex items-center gap-1 text-amber-700 text-[11px]">
                                                <Info size={12} /> exists — will update
                                            </span>
                                        ) : (
                                            <span className="text-neutral-400 text-[11px]">new</span>
                                        )}
                                        {r.done && (
                                            <div className="inline-flex items-center gap-1 text-emerald-700 text-[11px] mt-1">
                                                <CheckCircle2 size={12} /> saved
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            data-testid={SETUP.confirmBtn(i)}
                                            disabled={r.saving}
                                            onClick={() => applyRow(i)}
                                            className="px-3 py-1.5 text-[11px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-60"
                                            style={{ background: r.existing_id ? "#3D4A5C" : "var(--kp-red)" }}
                                        >
                                            {r.saving
                                                ? "Saving…"
                                                : r.existing_id
                                                ? "Update instead"
                                                : "Confirm & apply"}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
